/**
 * Trading Comps Skill Content — comparable companies valuation guidance.
 *
 * This is the domain knowledge the planner, executor, and verifier receive
 * when the planner fetches the "trading_comps" skill via `get_skill`. It covers:
 * peer set selection, multiples computation, calendarisation, summary statistics,
 * implied valuation, EV-to-equity bridge, depth calibration, update/edit traps,
 * sector-specific multiple conventions, and comps-specific validation rules.
 *
 * Condensed from the Notion Trading Comps Skill document, reconciled with
 * existing prompt guardrails (cell comments unreliable, formatting rules separate).
 */

export const TRADING_COMPS_SKILL_CONTENT = `## Skill: Trading Comps - Comparable Companies

### Purpose
Value a company by what the market pays today for comparable public businesses - apply peer trading multiples to the subject's metrics to derive an implied valuation range.

The analysis is only as good as the peer set and the like-for-like consistency of the multiples: a wrong peer poisons the median, and a numerator that doesn't match its denominator produces a number that means nothing. The output should be mechanically sound, point-in-time correct (struck on one date), and a live, formula-linked table where every multiple recomputes off matched metrics - so it is easy to refresh and reuse as a valuation leg.

### What Good Looks Like
A good comps analysis lets a reader answer five questions at a glance:
1. What is the subject worth on a relative basis, and how does that compare to where it trades?
2. Who are the peers, and why is each genuinely comparable?
3. What multiples, and where does the subject sit vs the set - cheap or expensive?
4. How does the chosen multiple translate to implied EV, equity and per share?
5. How wide is the range, and which multiple anchors it?

Deliver these sections in this order:
- Peer set with rationale: each peer justifiable, with documented reason for inclusion and notable exclusions.
- Multiples table: peers as rows; columns ordered size first (market cap, EV), then operating (revenue, EBITDA, growth, margin), then multiples (EV/Revenue, EV/EBITDA, EV/EBIT, P/E...), trailing then forward.
- Summary statistics: median and quartiles (and high/low), with n; subject excluded from its own stats; "not meaningful" multiples excluded, not averaged in.
- Calendarisation note: all peers on a common fiscal basis and the same period (LTM and/or NTM consistently).
- Implied valuation: apply the chosen statistic (usually median, or 25th-75th range) of each multiple to the subject's matching metric; for EV multiples bridge to equity and per share; triangulate across multiples into a range.
- Output summary: the implied range against current price with upside/downside, as-of date and currency noted.
- Key multiples visible: the one or two the sector trades on sit on the same screen as the output.
- Source line: data providers and "as of" date.
- Notes: adjustments, NM treatments and outlier handling called out as "(1), (2)..."

Every input carries visible provenance; every calculation is a formula linked to other cells, never a typed number.

### Calibrating Depth
Match comps complexity to the request. Do not run a fixed mode. Depth is a spectrum, not a menu.

**Invariants (always true at any depth):**
- Numerator matches denominator: EV multiples use enterprise value over a pre-financing metric (revenue, EBITDA, EBIT); equity multiples use price/equity value over a post-financing metric (net income, EPS, book equity). Never EV/net income, never P/EBITDA.
- Like-for-like across the set: every peer's multiple on the same calendar basis, same period (LTM or NTM consistently), same adjusted/reported convention, same currency and lease basis.
- EV built consistently: diluted market cap (TSM) + total debt + preferred + minority - cash, applied identically to every peer and the subject.
- Point-in-time: the whole set struck on one stated date; prices and estimates go stale daily.
- Stats are clean: subject excluded from its own statistics; NM/negative multiples excluded, not averaged in; median leads, not mean.
- Bridge holds: when an EV multiple drives an equity/per-share answer, bridge EV->equity in full.
- Every figure traces to a source, a linked cell, or a stated assumption.

**Depth spectrum:**
- Full comps: client-ready; rigorously screened and justified set (typically 8-15 peers), calendarised LTM and NTM multiples, quartile statistics, outlier treatment, implied range bridged to per share and sensitised.
- Focused comps: quick relative read; 4-6 closest peers, one or two headline multiples (EV/EBITDA, P/E) on forward consensus, median applied.
- Quick screen / single-multiple: ballpark; sector median forward EV/EBITDA x subject's EBITDA -> implied EV -> bridge to equity. Label indicative.
- User-handed peers / multiples: run exactly what's given; label indicative; sanity-check and flag per guardrails.

**Guardrails on shortcuts:**
- The peer set is the whole ball game - a few genuinely comparable peers beat a long loose list. Comparability (business model, size, growth, margins, geography) over count.
- Don't mix calendar bases, LTM/NTM, adjusted/reported or currency across the set.
- Mark negative or meaningless denominators "NM" and exclude them; prefer the median.
- Exclude or flag peers trading on something other than fundamentals (in play, distressed, illiquid micro-float).
- Don't apply a peer multiple to a mismatched subject metric or period.
- Run handed inputs even if the set looks loose, but flag them.

### FactSet Data Sourcing
When building a comps analysis, **use \`generate_formula\` to pull live data from FactSet** rather than typing static numbers. This is critical for a point-in-time correct, refreshable analysis.

**Always source from FactSet (via \`generate_formula\`):**
- Current share price for each peer and the subject (as-of date)
- Shares outstanding (diluted, treasury-stock method) for each peer and the subject
- Revenue, EBITDA, EBIT, EPS - both actuals (LTM/trailing) and consensus estimates (NTM, FY1, FY2) for each peer
- Net debt components per peer: total debt, cash, preferred equity, minority interests
- Market capitalisation (or compute from price x diluted shares)
- Consensus growth rates and margins where available
- Beta, if needed for context

**Acceptable as typed assumptions (no FactSet source needed):**
- Peer selection rationale (judgment, not data)
- Choice of which statistic to apply (median, quartile range)
- Adjustments to reported figures (one-offs, normalisation notes)
- The "as of" date itself (user choice of when to strike)
- Any user-provided override or scenario input

**Guidance:**
- Call \`generate_formula\` for each peer's data in the first build step - establish the full data set before computing multiples on top.
- Request all peers' data in a batch where possible to keep them on the same as-of date.
- Use the formula results as cell references in the multiples formulas - never re-type values.
- If FactSet returns #N/A for a peer's estimate, mark that multiple "NM" for that peer rather than substituting a guess.
- For consensus estimates (FY1, FY2, NTM), request the estimate items - FactSet covers consensus for most listed equities.
- For calendarisation: FactSet can provide calendarised estimates; request them on a common fiscal-year basis where possible.

### Build Procedure
1. **Define subject and pull peer data**: Fix the subject, metric basis, and as-of date. Use \`generate_formula\` to source current prices, diluted shares, net debt components, revenue/EBITDA/EBIT/EPS (actuals and consensus estimates) for each peer and the subject. Place in a data block as the single source of truth.
2. **Select and document peer set**: Screen by industry/sub-industry, refine by comparability (business model, end-markets, size, growth, margin, capital intensity, geography). Document inclusion/exclusion rationale. Exclude distorted names (in play, distressed, illiquid).
3. **Build EV per peer**: Diluted market cap (TSM) + total debt + preferred + minority - cash. Lease basis consistent. All referencing FactSet-sourced cells.
4. **Compute multiples live**: EV-or-price / metric - never typed. Trailing and forward, across the sector-relevant set. Each multiple is a formula referencing the data cells.
5. **Summarise**: Median, 25th/75th, high/low, mean and n. Exclude the subject from stats. Mark and exclude NM.
6. **Imply value**: Apply the chosen statistic to the subject's matching metric. For EV multiples bridge to equity and per share. Triangulate across multiples into a range.
7. **Validate**: Run the validation checks below.

### Conventions
- Enterprise value multiples: EV/Revenue, EV/EBITDA, EV/EBIT, EV/EBITDAR - pair EV with a pre-interest metric available to all capital providers.
- Equity multiples: P/E, P/B, P/FCFE, dividend yield, P/NAV - pair price/equity with a post-interest, equity-only metric.
- Forward over trailing: markets trade on the forward view; lead with NTM or FY1/FY2, show trailing (LTM) for context.
- EV construction: diluted market cap (TSM on in-the-money options/RSUs, if-converted on convertibles) + total debt + preferred + minority - cash & equivalents; lease-consistent.
- Statistics: median is the headline (robust to outliers); show quartiles, high/low and n. Exclude subject. Mark NM and exclude.
- Point-in-time: prices and estimates as of one stated date. Re-strike entirely on refresh.
- Currency & units: all peers in same currency (or FX-adjusted); units labelled in headers.

### Multiples by Sector
Lead with the basis the sector actually trades on:
- General / industrials: EV/EBITDA, EV/EBIT, P/E, FCF yield, PEG
- Software / SaaS: EV/Revenue, EV/ARR (growth-adjusted), EV/Gross Profit; P/E only once profitable
- Consumer / retail: EV/EBITDA, EV/EBITDAR (lease-adjusted), P/E, EV/Sales
- Banks: P/E, P/TBV (price/tangible book) vs ROTE; EV not meaningful
- Insurance: P/BV, P/EV (embedded value), P/E
- REITs / real estate: P/FFO, P/AFFO, P/NAV, implied cap rate, dividend yield
- Energy / E&P: EV/EBITDX, EV/DACF, EV/proved reserves, EV/production
- Telecom: EV/EBITDA, EV/(EBITDA - capex), EV/subscriber
- Pharma / biotech: EV/Revenue, P/E for commercial; pipeline = rNPV, not a multiple
- Asset managers: P/E, EV/EBITDA, % of AUM
- Utilities: P/E, EV/EBITDA, P/B, dividend yield, EV/RAB (regulated)
- Semiconductors: EV/Sales, EV/EBITDA, P/E - normalise for cyclicality
- Marketplaces / platforms: EV/GMV, EV/Revenue, EV/EBITDA

**Edge cases:**
- Loss-making / negative EBITDA: use EV/Revenue or EV/Gross Profit and forward multiples; mark current EV/EBITDA NM.
- High-growth: EV/Revenue with growth adjustment; beware static P/E.
- Cyclicals: normalise to mid-cycle; trailing peak/trough mislead.
- Holding companies / conglomerates: sum-of-the-parts, not one blended multiple.
- Net-cash companies: EV below equity value is fine, check sign.
- Companies in play: trade on a bid; exclude or flag.
- Thin sets (<4 peers): say so, lean on other methods.

### Updating & Editing a Comps Analysis
- Re-strike the whole set on one current date. A half-refreshed table mixes dates and is wrong.
- Re-pull estimates as they roll (NTM rolls forward, FY1 becomes actual).
- Re-screen the set periodically (peers get acquired, go private, drift out of comparability).
- Changing the chosen statistic or applied multiple should move only the implied range - if a peer's multiple changes when you didn't touch it, a price or estimate has been hardcoded.

### Failure Modes
| Symptom | Likely Cause | Fix |
|---|---|---|
| Multiple is nonsense | Numerator/denominator mismatch (EV/net income, P/EBITDA) | Match EV to pre-interest, price to post-interest |
| Median looks off | Wrong/loose peer set | Re-screen; cut false peers; justify each |
| Peers not comparable | Off-cycle year-ends not calendarised | Calendarise to common reference |
| Stats distorted | NM/negative multiples averaged in | Exclude NM; prefer median |
| Inconsistent EBITDA | Adjusted for some, reported for others | One convention across set |
| Wrong basis | Trailing and forward mixed in one stat | Keep each stat on one horizon |
| Market cap wrong | Basic instead of diluted shares | Diluted (treasury-stock method) |
| Circular range | Subject included in its own stats | Exclude the subject |
| Table worthless | Stale prices not refreshed | Re-strike whole set on one date |
| Implied value off | Peer multiple applied to mismatched subject metric/period | NTM multiple -> NTM metric; bridge EV->equity for per share |

### Validation Rules

**Hard fails (block output):**
- No formula errors (#REF!, #DIV/0!, #VALUE!, #N/A, #NAME?).
- Numerator/denominator consistency: every EV multiple pairs EV with a pre-interest metric; every equity multiple pairs price/equity with a post-interest metric.
- EV built consistently across all peers and the subject (diluted equity + debt + preferred + minority - cash; lease basis consistent).
- Diluted shares (TSM) used for every market cap.
- Multiples computed live (EV-or-price / metric) - no hardcoded multiples.
- The subject is excluded from summary statistics; NM/negative multiples excluded, not averaged.
- Implied valuation applies each multiple to the subject's matching metric and period; EV multiples bridge to equity (ties).

**Warnings (flag, do not block):**
- A peer multiple is a statistical outlier (well outside interquartile range) - review or trim.
- Mean and median diverge materially - lead with median.
- A peer flagged as in-play, distressed or illiquid.
- Trailing and forward mixed within a single statistic.
- Fewer than ~4-5 peers, or a visibly heterogeneous set.
- Adjusted-vs-reported EBITDA inconsistency across the set.
- Prices/estimates as-of date is stale.
- Peer data (prices, shares, estimates, net debt) is hardcoded instead of sourced from FactSet via \`generate_formula\` - the table will not refresh.`;
